import React, { useEffect, useState, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setSearchFilter,
  setLocationFilter,
  setDistributorTypeFilter,
  setStatusFilter,
  fetchIncentives,
} from '../../store/incentiveSlice';
import { fetchDistributorChannels } from '../../store/distributorChannelSlice';
import { getZonesAPI } from '../../api/locations';
import { CustomDropdown } from '../Common/CustomDropdown';

export const IncentiveFilters: React.FC = () => {
  const dispatch = useAppDispatch();
  const { filters } = useAppSelector((state) => state.incentive);
  const { channels } = useAppSelector((state) => state.channels);

  const [zonesList, setZonesList] = useState<string[]>([]);

  useEffect(() => {
    if (!channels || channels.length === 0) {
      dispatch(fetchDistributorChannels());
    }
    const loadDropdownData = async () => {
      try {
        const zonesRes = await getZonesAPI();
        if (zonesRes && zonesRes.data) {
          const names = zonesRes.data.map((z: any) => z.name);
          setZonesList(names);
        }
      } catch (e) {
        console.warn('Could not load zones dropdown for incentive filter:', e);
      }
    };
    loadDropdownData();
  }, [dispatch, channels]);

  const distributorTypeOptions = useMemo(() => {
    if (channels && channels.length > 0) {
      const channelNames = channels.map((c: any) => c.name || c.channelName || String(c)).filter(Boolean);
      return ['All Types', ...Array.from(new Set(channelNames))];
    }
    return ['All Types', 'Distributor + Dealer', 'General Trade (GT)', 'Modern Trade (MT)', 'testing channel'];
  }, [channels]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchFilter(e.target.value));
    dispatch(fetchIncentives());
  };

  const handleLocationChange = (val: string) => {
    dispatch(setLocationFilter(val));
    dispatch(fetchIncentives());
  };

  const handleDistributorTypeChange = (val: string) => {
    dispatch(setDistributorTypeFilter(val));
    dispatch(fetchIncentives());
  };

  const handleStatusChange = (val: string) => {
    dispatch(setStatusFilter(val));
    dispatch(fetchIncentives());
  };

  return (
    <div className="bg-white rounded-2xl p-4 md:p-5 mb-6 shadow-sm border border-gray-100">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
        {/* Incentive ID / Search Input */}
        <div>
          <label className="block text-gray-700 font-semibold text-xs mb-1.5">
            Incentive ID
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder="INC-2025-018..."
              value={filters.search}
              onChange={handleSearchChange}
              className="w-full border border-gray-300 rounded-xl py-2.5 px-3.5 outline-none focus:border-[#B71C1C] focus:ring-1 focus:ring-[#B71C1C] text-xs font-semibold text-gray-800 placeholder-gray-400 bg-white transition-colors"
            />
          </div>
        </div>

        {/* Zone Dropdown */}
        <CustomDropdown
          label="Zone"
          placeholder="All Regions"
          options={['All Regions', ...zonesList]}
          value={filters.location || 'All Regions'}
          onChange={handleLocationChange}
        />

        {/* Distributor Type Dropdown */}
        <CustomDropdown
          label="Distributor Type"
          placeholder="All Types"
          options={distributorTypeOptions}
          value={filters.distributorType || 'All Types'}
          onChange={handleDistributorTypeChange}
          searchable={true}
        />

        {/* Incentive Status Dropdown */}
        <CustomDropdown
          label="Incentive Status"
          placeholder="Status"
          options={['Status', 'Active', 'Inactive', 'Expired', 'Upcoming']}
          value={filters.status || 'Status'}
          onChange={handleStatusChange}
          searchable={false}
        />
      </div>
    </div>
  );
};
